import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { useAuth } from "../lib/auth";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import NonprofitAuctionCTA from "../components/NonprofitAuctionCTA";
import AuctioneerWhiteLabelCTA from "../components/AuctioneerWhiteLabelCTA";
import { ArrowLeft, MapPin, Globe, Phone, EnvelopeSimple, CheckCircle, Gavel, Crown, LockKey, X, Star, ChatCircle, Plus, Trash } from "@phosphor-icons/react";

const STATE_NAMES = {AL:"Alabama",AK:"Alaska",AZ:"Arizona",AR:"Arkansas",CA:"California",CO:"Colorado",CT:"Connecticut",DE:"Delaware",FL:"Florida",GA:"Georgia",HI:"Hawaii",ID:"Idaho",IL:"Illinois",IN:"Indiana",IA:"Iowa",KS:"Kansas",KY:"Kentucky",LA:"Louisiana",ME:"Maine",MD:"Maryland",MA:"Massachusetts",MI:"Michigan",MN:"Minnesota",MS:"Mississippi",MO:"Missouri",MT:"Montana",NE:"Nebraska",NV:"Nevada",NH:"New Hampshire",NJ:"New Jersey",NM:"New Mexico",NY:"New York",NC:"North Carolina",ND:"North Dakota",OH:"Ohio",OK:"Oklahoma",OR:"Oregon",PA:"Pennsylvania",RI:"Rhode Island",SC:"South Carolina",SD:"South Dakota",TN:"Tennessee",TX:"Texas",UT:"Utah",VT:"Vermont",VA:"Virginia",WA:"Washington",WV:"West Virginia",WI:"Wisconsin",WY:"Wyoming"};

export default function AuctioneerProfilePage() {
    const { slug } = useParams();
    const { user } = useAuth();
    const [search, setSearch] = useSearchParams();
    const [a, setA] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [availability, setAvailability] = useState(null);
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [reviewSummary, setReviewSummary] = useState({ total: 0, avg: 0, distribution: {1:0,2:0,3:0,4:0,5:0} });
    const [reviewOpen, setReviewOpen] = useState(false);

    const loadReviews = useCallback(async () => {
        try {
            const [r1, r2] = await Promise.all([
                api.get(`/auctioneers/${slug}/reviews`),
                api.get(`/auctioneers/${slug}/reviews/summary`),
            ]);
            setReviews(r1.data?.rows || []);
            setReviewSummary(r2.data || { total: 0, avg: 0, distribution: {1:0,2:0,3:0,4:0,5:0} });
        } catch (_) { /* ignore */ }
    }, [slug]);

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/auctioneers/${slug}`);
            setA(r.data);
            const av = await api.get(`/auctioneer-featured/availability/${r.data.state}/${r.data.city_slug}`);
            setAvailability(av.data);
        } catch (e) {
            // Retired/renamed slug → forward to the current listing if we can match it.
            try {
                const res = await api.get("/auctioneers/legacy-resolve", { params: { path: `/auctioneer/${slug}` } });
                const to = res.data?.to;
                if (to && to !== `/auctioneer/${slug}`) { window.location.replace(to); return; }
            } catch (_) { /* ignore */ }
            setNotFound(true);
        }
    }, [slug]);

    useEffect(() => { load(); loadReviews(); }, [load, loadReviews]);

    // Auto-open checkout modal when arrived via renewal email link
    useEffect(() => {
        if (search.get("renew") === "1" && a && !checkoutOpen) {
            setCheckoutOpen(true);
            const next = new URLSearchParams(search);
            next.delete("renew");
            setSearch(next, { replace: true });
        }
    }, [a, search, checkoutOpen, setSearch]);

    // Poll payment status after returning from Stripe
    useEffect(() => {
        const sid = search.get("featured_session");
        if (!sid || paymentStatus) return;
        setPaymentStatus("polling");
        let attempts = 0;
        const tick = async () => {
            attempts++;
            try {
                const r = await api.get(`/auctioneer-featured/status/${sid}`);
                if (r.data?.status === "paid" || r.data?.payment_status === "paid") {
                    setPaymentStatus("paid");
                    trackEvent("purchase", {
                        transaction_id: sid,
                        currency: "USD",
                        value: 99,
                        items: [{
                            item_id: `featured_${a?.state}_${a?.city_slug}`,
                            item_name: `Featured slot — ${a?.city}, ${a?.state}`,
                            item_category: "auctioneer_featured",
                            price: 99,
                            quantity: 1,
                        }],
                    });
                    setTimeout(load, 800);
                    return;
                }
            } catch (_) { /* ignore */ }
            if (attempts < 8) setTimeout(tick, 2000);
            else setPaymentStatus("failed");
        };
        tick();
        // Clear query param so refresh doesn't re-poll
        const next = new URLSearchParams(search);
        next.delete("featured_session");
        setSearch(next, { replace: true });
    }, [search, paymentStatus, setSearch, load]);

    if (notFound) return (
        <div className="p-12 text-center" data-testid="auctioneer-not-found">
            <Helmet>
                <title>Auctioneer listing not found · CharityFundraiser</title>
                <meta name="robots" content="noindex,follow" />
            </Helmet>
            <p className="text-foreground/55 mb-4">This auctioneer listing was not found.</p>
            <Link to="/find-an-auctioneer" className="text-accent underline" data-testid="auctioneer-not-found-back">Back to the directory</Link>
        </div>
    );
    if (!a) return <div className="p-12 text-center text-foreground/55">Loading…</div>;

    const stateName = STATE_NAMES[a.state] || a.state;
    const isFeatured = !!a.featured;
    const specialtyText = (a.specialties || []).length
        ? ` Specialties include ${a.specialties.slice(0, 5).join(", ")}.`
        : "";
    // Always render a unique, location-specific About paragraph so even imported
    // listings without an owner-written bio are not "thin" pages for Google.
    const aboutText = a.bio || `${a.company} is a benefit auctioneer serving nonprofits, schools, and charities in ${a.city}, ${stateName}. They help organizations raise more at galas, silent and live auctions, paddle raises, and fund-a-need appeals.${specialtyText} ${a.company} is listed in CharityFundraiser's free, vetted directory of benefit auctioneers so your team can compare local professionals, read verified coordinator reviews, and book with confidence.`;
    const faqs = [
        { q: "What does a benefit auctioneer do?",
          a: `A benefit auctioneer runs the live auction and "fund-a-need" paddle raise at a nonprofit fundraising event. A skilled benefit auctioneer like ${a.company} reads the room, builds energy, and maximizes the dollars raised for your cause in ${a.city}, ${stateName}.` },
        { q: `How much does a benefit auctioneer cost in ${a.city}, ${stateName}?`,
          a: `Fees vary by event size and format — many charge a flat event fee or a percentage of funds raised. Contact ${a.company} through this listing for a quote tailored to your ${a.city} event.` },
        { q: `How do I book ${a.company}?`,
          a: `Use the contact details on this page to reach ${a.company}${a.contact_masked ? " once the listing is claimed" : ""}. We recommend booking your benefit auctioneer 2–3 months before your event date.` },
    ];
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": a.company,
        "description": aboutText.slice(0, 300),
        "url": `https://charityfundraiser.com/auctioneer/${a.slug}`,
        "address": {
            "@type": "PostalAddress",
            "streetAddress": a.street || "",
            "addressLocality": a.city,
            "addressRegion": a.state,
            "postalCode": a.postal_code || "",
            "addressCountry": "US",
        },
        "telephone": a.phone || undefined,
        "email": a.email || undefined,
        "sameAs": a.website ? [a.website] : undefined,
        "aggregateRating": reviewSummary.total > 0 ? {
            "@type": "AggregateRating",
            "ratingValue": reviewSummary.avg,
            "reviewCount": reviewSummary.total,
        } : undefined,
    };
    const faqLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(f => ({
            "@type": "Question", "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
    };

    return (
        <div className="bg-secondary/15 min-h-screen">
            <Navbar />
            <Helmet>
                <title>{`${a.company} — ${a.city}, ${a.state} Auctioneer · CharityFundraiser`}</title>
                <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1" />
                <meta name="description" content={`${a.company} is a benefit auctioneer based in ${a.city}, ${stateName}. ${a.bio?.slice(0, 140) || "Browse contact info, specialty, and credentials."}`} />
                <link rel="canonical" href={`https://charityfundraiser.com/auctioneer/${a.slug}`} />
                <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
                <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
            </Helmet>

            {paymentStatus === "paid" && (
                <div className="bg-green-100 border-b border-green-300 px-6 py-3 text-sm text-green-900" data-testid="featured-paid-banner">
                    Payment confirmed — you're now the Featured Auctioneer for {a.city}, {a.state} for the next 12 months. Thank you!
                </div>
            )}
            {paymentStatus === "polling" && (
                <div className="bg-blue-100 border-b border-blue-300 px-6 py-3 text-sm text-blue-900">
                    Confirming your payment with Stripe…
                </div>
            )}
            {search.get("featured_cancelled") === "1" && (
                <div className="bg-amber-100 border-b border-amber-300 px-6 py-3 text-sm text-amber-900">
                    Checkout cancelled — no charge was made.
                </div>
            )}

            <section className="bg-primary text-primary-foreground pt-32 pb-12 px-6">
                <div className="max-w-3xl mx-auto">
                    <Link to={`/find-an-auctioneer/${a.state.toLowerCase()}/${a.city_slug}`} className="text-xs label-eyebrow text-accent hover:underline flex items-center gap-1.5 mb-3"><ArrowLeft size={12}/> Back to {a.city} auctioneers</Link>
                    <p className="label-eyebrow text-accent flex items-center gap-1.5"><Gavel size={12}/> Auctioneer</p>
                    <h1 className="font-heading text-4xl sm:text-5xl mt-2">{a.company}</h1>
                    {reviewSummary.total > 0 && (
                        <div className="flex items-center gap-2 mt-3" data-testid="hero-rating">
                            <StarRow value={reviewSummary.avg} size={16} />
                            <span className="text-sm text-white/85"><strong>{reviewSummary.avg}</strong>/5</span>
                            <span className="text-xs text-white/65">· {reviewSummary.total} review{reviewSummary.total === 1 ? "" : "s"}</span>
                        </div>
                    )}
                    <div className="flex flex-wrap gap-2 mt-4">
                        {isFeatured && <span className="text-[10px] uppercase tracking-wider font-bold bg-amber-400 text-primary px-2 py-1 flex items-center gap-1" data-testid="featured-badge"><Crown size={10} weight="fill"/> Featured in {a.city}</span>}
                        {a.verified && <span className="text-[10px] uppercase tracking-wider font-bold bg-accent text-accent-foreground px-2 py-1 flex items-center gap-1"><CheckCircle size={10} weight="fill"/> Verified by CharityFundraiser</span>}
                        {a.pending && <span className="text-[10px] uppercase tracking-wider font-bold bg-white/15 text-white/85 px-2 py-1">Pending verification</span>}
                        {a.claimed && !a.verified && !isFeatured && <span className="text-[10px] uppercase tracking-wider font-bold bg-white/15 text-white/85 px-2 py-1">Claimed</span>}
                    </div>
                    <p className="text-white/85 text-sm mt-4"><MapPin size={12} className="inline mr-1"/>{a.city}, {stateName}</p>
                </div>
            </section>

            <section className="max-w-3xl mx-auto py-10 px-6 grid md:grid-cols-3 gap-8">
                <div className="md:col-span-2 space-y-6">
                    <div className="bg-white border border-border p-5" data-testid="auctioneer-about">
                        <p className="label-eyebrow text-[10px] mb-2">About</p>
                        <p className="text-sm leading-relaxed whitespace-pre-line">{aboutText}</p>
                    </div>
                    {a.license_text && (
                        <div className="bg-white border border-border p-5">
                            <p className="label-eyebrow text-[10px] mb-2">Licenses & Certifications</p>
                            <p className="text-sm">{a.license_text}</p>
                        </div>
                    )}
                    {(a.specialties || []).length > 0 && (
                        <div className="bg-white border border-border p-5">
                            <p className="label-eyebrow text-[10px] mb-2">Specialties</p>
                            <div className="flex flex-wrap gap-2">
                                {a.specialties.map(s => <span key={s} className="text-xs bg-secondary/30 px-3 py-1">{s}</span>)}
                            </div>
                        </div>
                    )}

                    {/* Pricing — self-reported fee structure */}
                    {a.pricing && a.pricing.model && (
                        <PricingCard pricing={a.pricing} />
                    )}

                    {/* FAQ — unique, location-specific content + FAQPage rich-result eligibility */}
                    <div className="bg-white border border-border p-5" data-testid="auctioneer-faq">
                        <p className="label-eyebrow text-[10px] mb-3">Frequently asked questions</p>
                        <div className="space-y-4">
                            {faqs.map((f, i) => (
                                <div key={i} data-testid={`auctioneer-faq-${i}`}>
                                    <p className="text-sm font-medium">{f.q}</p>
                                    <p className="text-sm text-foreground/70 mt-1 leading-relaxed">{f.a}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Reviews from nonprofit event coordinators */}
                    <ReviewsSection
                        slug={slug}
                        company={a.company}
                        reviews={reviews}
                        summary={reviewSummary}
                        canReview={user?.role === "org_admin"}
                        isSuper={user?.role === "super_admin"}
                        onOpenWriteReview={() => setReviewOpen(true)}
                        onSeeded={loadReviews}
                    />

                    {/* Featured CTA — show if NOT already featured, and slot is available OR renewing */}
                    {(!isFeatured && availability && (availability.available || availability.current_holder?.auctioneer_slug === a.slug)) && (
                        <div className="relative overflow-hidden bg-primary text-primary-foreground border-2 border-amber-400 shadow-lg" data-testid="featured-cta">
                            <div className="absolute top-0 right-0 bg-amber-400 text-primary px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold flex items-center gap-1.5">
                                <Crown size={11} weight="fill"/> Exclusive
                            </div>
                            <div className="p-6 md:p-8">
                                <div className="flex items-center gap-2 text-amber-400 mb-3">
                                    <Crown size={20} weight="fill"/>
                                    <p className="label-eyebrow text-amber-400 text-xs">Become THE Featured Auctioneer</p>
                                </div>
                                <h3 className="font-heading text-2xl md:text-3xl leading-tight mb-2">
                                    Own the spotlight in {a.city}, {a.state}
                                </h3>
                                <p className="text-sm text-white/85 leading-relaxed mb-4">
                                    One auctioneer per city earns the Featured slot — <strong className="text-white">top placement on every search</strong>, gold Crown badge on every listing, and your contact info shown directly to every nonprofit browsing your area. <strong className="text-amber-400">{availability.current_holder?.auctioneer_slug === a.slug ? "Renew to extend your slot." : `The ${a.city}, ${a.state} slot is available right now.`}</strong>
                                </p>
                                <ul className="text-xs space-y-1.5 text-white/75 mb-5">
                                    <li className="flex items-center gap-2"><CheckCircle size={12} weight="fill" className="text-amber-400 shrink-0"/> Top of every {a.city} & {a.state} directory result</li>
                                    <li className="flex items-center gap-2"><CheckCircle size={12} weight="fill" className="text-amber-400 shrink-0"/> Crown badge — visual proof of premium status</li>
                                    <li className="flex items-center gap-2"><CheckCircle size={12} weight="fill" className="text-amber-400 shrink-0"/> Contact info revealed (claimed auto-granted)</li>
                                    <li className="flex items-center gap-2"><CheckCircle size={12} weight="fill" className="text-amber-400 shrink-0"/> Renewal nudges at T-30 / T-7 / T-1 day</li>
                                    <li className="flex items-center gap-2"><CheckCircle size={12} weight="fill" className="text-amber-400 shrink-0"/> No auto-renew — you stay in control</li>
                                </ul>
                                <div className="flex flex-wrap items-end gap-4">
                                    <div>
                                        <p className="text-[10px] uppercase tracking-wider text-amber-400">Only</p>
                                        <p className="font-heading text-4xl md:text-5xl data-num text-amber-400 leading-none">
                                            $99<span className="text-base text-white/65 align-top">/yr</span>
                                        </p>
                                        <p className="text-[10px] text-white/55 mt-1">≈ $8.25/mo · 1 city · 12 months</p>
                                    </div>
                                    <button onClick={() => setCheckoutOpen(true)} className="bg-amber-400 hover:bg-amber-300 text-primary px-6 py-3.5 text-xs uppercase tracking-[0.2em] font-bold inline-flex items-center gap-2 transition-colors" data-testid="featured-cta-btn">
                                        <Crown size={14} weight="fill"/> Claim this spot
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Slot taken by another auctioneer */}
                    {!isFeatured && availability && !availability.available && availability.current_holder && availability.current_holder.auctioneer_slug !== a.slug && (
                        <div className="bg-secondary/30 border border-border p-5 text-sm">
                            <p className="text-foreground/70">
                                <Crown size={12} weight="fill" className="inline mr-1 text-amber-500"/>
                                The Featured spot for {a.city}, {a.state} is currently held by <Link to={`/auctioneer/${availability.current_holder.auctioneer_slug}`} className="text-accent underline">{availability.current_holder.company}</Link>.
                                Available again on {String(availability.current_holder.featured_until).slice(0, 10)}.
                            </p>
                        </div>
                    )}

                    {!a.claimed && (
                        <div className="bg-accent/10 border border-accent/30 p-5">
                            <p className="font-medium text-accent">Is this your business?</p>
                            <p className="text-sm mt-1">Claim your listing (free) to add a bio, photos, and reveal your contact info to nonprofits searching the directory.</p>
                            <Link to={`/find-an-auctioneer/join?claim_id=${a.id}`} className="inline-block mt-3 bg-accent text-accent-foreground px-4 py-2 text-xs uppercase tracking-wider font-bold" data-testid="claim-cta">
                                Claim this listing — free
                            </Link>
                        </div>
                    )}
                </div>

                <aside className="space-y-3">
                    <div className="bg-white border border-border p-5">
                        <p className="label-eyebrow text-[10px] mb-3">Contact</p>
                        {a.contact_name && <p className="text-sm font-medium mb-3">{a.contact_name}</p>}
                        {a.contact_masked ? (
                            <div className="bg-secondary/30 border border-dashed border-border p-3 mb-2 text-xs text-foreground/65 flex items-start gap-2" data-testid="contact-masked">
                                <LockKey size={14} className="text-accent shrink-0 mt-0.5"/>
                                <div>
                                    Contact info is hidden until the auctioneer claims this listing.
                                    <Link to={`/find-an-auctioneer/join?claim_id=${a.id}`} className="text-accent underline block mt-1">If this is you, claim it free →</Link>
                                </div>
                            </div>
                        ) : (
                            <>
                                {a.phone && <p className="text-sm mb-2"><Phone size={12} className="inline mr-2 text-accent"/><a href={`tel:${a.phone}`} className="hover:underline" data-testid="profile-phone">{a.phone}</a></p>}
                                {a.email && <p className="text-sm mb-2"><EnvelopeSimple size={12} className="inline mr-2 text-accent"/><a href={`mailto:${a.email}`} className="hover:underline break-all" data-testid="profile-email">{a.email}</a></p>}
                            </>
                        )}
                        {a.website && <p className="text-sm mb-2"><Globe size={12} className="inline mr-2 text-accent"/><a href={a.website} target="_blank" rel="noopener noreferrer" className="hover:underline break-all" data-testid="profile-website">{a.website}</a></p>}
                        {a.street && <p className="text-xs text-foreground/65 mt-3 leading-relaxed">{a.street}<br/>{a.city}, {a.state} {a.postal_code}</p>}
                    </div>

                    {isFeatured && a.featured_until && (
                        <div className="bg-amber-50 border border-amber-200 p-4 text-xs">
                            <Crown size={12} weight="fill" className="text-amber-500 inline mr-1"/>
                            Featured slot active until <strong>{String(a.featured_until).slice(0, 10)}</strong>.
                        </div>
                    )}
                </aside>
            </section>

            {(a.nearby || []).length > 0 && (
                <section className="max-w-3xl mx-auto py-6 px-6 pb-16">
                    <h2 className="font-heading text-xl mb-3">Other auctioneers in {a.city}</h2>
                    <div className="grid sm:grid-cols-2 gap-2">
                        {a.nearby.map(n => (
                            <Link key={n.id} to={`/auctioneer/${n.slug}`} className="bg-white border border-border hover:border-accent p-3 text-sm transition-colors">
                                <p className="font-medium flex items-center gap-1.5">
                                    {n.featured && <Crown size={10} weight="fill" className="text-amber-500"/>}
                                    {n.company}
                                </p>
                                <p className="text-xs text-foreground/55 mt-1">{n.city}, {n.state}</p>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            <NonprofitAuctionCTA context={a.city && a.state ? `in ${a.city}, ${a.state}` : ""} />
            <AuctioneerWhiteLabelCTA />

            {checkoutOpen && (
                <FeaturedCheckoutModal a={a} availability={availability} onClose={() => setCheckoutOpen(false)} />
            )}
            {reviewOpen && (
                <WriteReviewModal
                    slug={slug}
                    company={a.company}
                    onClose={() => setReviewOpen(false)}
                    onSubmitted={async () => { setReviewOpen(false); await loadReviews(); }}
                />
            )}

            <Footer />
        </div>
    );
}

function FeaturedCheckoutModal({ a, availability, onClose }) {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr("");
        try {
            trackEvent("begin_checkout", {
                currency: "USD",
                value: availability?.price_per_year_usd || 99,
                items: [{
                    item_id: `featured_${a.state}_${a.city_slug}`,
                    item_name: `Featured slot — ${a.city}, ${a.state}`,
                    item_category: "auctioneer_featured",
                    price: availability?.price_per_year_usd || 99,
                    quantity: 1,
                }],
                auctioneer_id: a.id,
                state: a.state,
                city: a.city,
            });
            const r = await api.post("/auctioneer-featured/checkout", {
                auctioneer_id: a.id,
                state: a.state,
                city: a.city,
                origin_url: window.location.origin,
                contact_email: email || undefined,
            });
            if (r.data?.url) {
                window.location.href = r.data.url;
            } else {
                throw new Error("No checkout URL returned");
            }
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "Checkout failed");
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
            <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-card border border-border max-w-md w-full p-6 space-y-4" data-testid="featured-checkout-modal">
                <div className="flex items-start justify-between">
                    <div>
                        <p className="label-eyebrow text-amber-700 flex items-center gap-1.5"><Crown size={12} weight="fill"/> Featured slot</p>
                        <h2 className="font-heading text-2xl mt-1">{a.city}, {a.state}</h2>
                    </div>
                    <button type="button" onClick={onClose}><X size={20}/></button>
                </div>
                <div className="text-sm text-foreground/70 space-y-2">
                    <p>You're purchasing the <strong>Featured Auctioneer slot</strong> for <strong>{a.city}, {a.state}</strong> for 12 months.</p>
                    <ul className="text-xs space-y-1 list-disc pl-4 text-foreground/65">
                        <li>Top placement in {a.city} & {a.state} search results</li>
                        <li>Gold Crown badge on every listing</li>
                        <li>Contact info revealed (claimed status auto-granted)</li>
                        <li>One-time payment, no auto-renew. We'll email you a renewal nudge 30 days before expiry.</li>
                    </ul>
                </div>
                <div className="bg-secondary/20 p-3 text-sm space-y-1.5">
                    <div className="flex justify-between">
                        <span>{a.city}, {a.state} Featured · 12 months</span>
                        <span>${availability?.price_per_year_usd ?? 99}.00</span>
                    </div>
                    <div className="flex justify-between text-xs text-foreground/65">
                        <span>Card processing fee (2.9% + $0.30)</span>
                        <span>${(availability?.card_fee_usd ?? 3.17).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between border-t border-border pt-1.5 mt-1 font-bold">
                        <span>Total charged</span>
                        <span>${(availability?.total_usd ?? 102.17).toFixed(2)}</span>
                    </div>
                </div>
                <label className="block">
                    <span className="text-xs label-eyebrow">Email for receipt (optional)</span>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={a.email || "you@example.com"} className="ed-input w-full mt-1" data-testid="featured-checkout-email"/>
                </label>
                {err && <p className="text-destructive text-sm">{err}</p>}
                <button type="submit" disabled={busy} className="w-full bg-amber-500 hover:bg-amber-600 text-primary px-5 py-3 text-xs uppercase tracking-wider font-bold disabled:opacity-50" data-testid="featured-checkout-submit">
                    {busy ? "Opening Stripe…" : `Continue to Stripe — $${(availability?.total_usd ?? 102.17).toFixed(2)}`}
                </button>
                <p className="text-[11px] text-foreground/50 text-center">Powered by Stripe · Card details stay on Stripe's servers, never touch ours.</p>
            </form>
        </div>
    );
}


const FIXED_LABEL = (r) => r?.endsWith?.("+") ? `$${r.slice(0, -1).replace(/(\d+)/, (m) => Number(m).toLocaleString())}+` : `$${r.split("-").map(n => Number(n).toLocaleString()).join(" – $")}`;

function PricingCard({ pricing }) {
    if (!pricing || !pricing.model) return null;
    const m = pricing.model;
    const showFixed = (m === "fixed" || m === "both") && pricing.fixed_by_audience && Object.keys(pricing.fixed_by_audience).length > 0;
    const showPct = (m === "percentage" || m === "both") && pricing.percentage_of_gross;
    const isContact = m === "contact";

    return (
        <div className="bg-white border-2 border-accent/40 p-5" data-testid="pricing-card">
            <div className="flex items-center gap-2 mb-1">
                <CheckCircle size={14} weight="fill" className="text-accent"/>
                <p className="label-eyebrow text-[10px]">Fees & Pricing</p>
            </div>
            {isContact ? (
                <p className="text-sm">Custom pricing — please contact directly for a quote.</p>
            ) : (
                <>
                    {showFixed && (
                        <div className="mt-2">
                            <p className="text-xs font-medium text-foreground/80 mb-2">
                                Fixed fee by event size{m === "both" && " — or —"}
                            </p>
                            <table className="w-full text-xs">
                                <tbody>
                                    {Object.entries(pricing.fixed_by_audience).map(([bracket, fee]) => (
                                        <tr key={bracket} className="border-b border-border/50 last:border-b-0">
                                            <td className="py-1.5 text-foreground/65 font-mono">{bracket} guests</td>
                                            <td className="py-1.5 text-right font-bold data-num">{FIXED_LABEL(fee)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {showPct && (
                        <div className={`${showFixed ? "mt-4 pt-3 border-t border-border" : "mt-2"}`}>
                            <p className="text-xs font-medium text-foreground/80 mb-1">Percentage-based fee</p>
                            <p className="font-heading text-2xl data-num text-accent">{pricing.percentage_of_gross}% <span className="text-xs text-foreground/65 font-body">of gross sales</span></p>
                        </div>
                    )}
                </>
            )}
            {pricing.notes && (
                <p className="text-[11px] text-foreground/60 mt-3 italic leading-relaxed border-t border-border pt-2">
                    {pricing.notes}
                </p>
            )}
            <p className="text-[10px] text-foreground/45 mt-2 italic">Self-reported by the auctioneer · Confirm details when booking.</p>
        </div>
    );
}


function StarRow({ value = 0, size = 14, onChange = null }) {
    const interactive = !!onChange;
    const stars = [1, 2, 3, 4, 5].map(n => {
        const filled = n <= Math.round(value);
        const Cmp = (
            <Star
                size={size}
                weight={filled ? "fill" : "regular"}
                className={filled ? "text-amber-400" : "text-white/30"}
            />
        );
        return interactive ? (
            <button type="button" key={n} onClick={() => onChange(n)} className="hover:scale-110 transition-transform" data-testid={`star-${n}`}>
                {Cmp}
            </button>
        ) : <span key={n}>{Cmp}</span>;
    });
    return <span className="inline-flex items-center gap-0.5" data-testid="star-row">{stars}</span>;
}

function VerificationBadge({ tier, event_name, event_date }) {
    if (tier === "event_linked") {
        const date = event_date ? new Date(event_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";
        return (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-green-700 bg-green-50 border border-green-300 px-2 py-0.5" data-testid="vbadge-event-linked">
                <CheckCircle size={10} weight="fill"/> Verified — worked {event_name}{date && ` on ${date}`}
            </span>
        );
    }
    if (tier === "coordinator") {
        return (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-blue-700 bg-blue-50 border border-blue-300 px-2 py-0.5" data-testid="vbadge-coordinator">
                <CheckCircle size={10} weight="fill"/> Verified Nonprofit Coordinator
            </span>
        );
    }
    return null;
}

function ReviewsSection({ slug, company, reviews, summary, canReview, isSuper, onOpenWriteReview, onSeeded }) {
    const [seedOpen, setSeedOpen] = useState(false);
    return (
        <div className="bg-white border border-border p-5" data-testid="reviews-section">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                    <p className="label-eyebrow text-[10px] flex items-center gap-1.5"><Star size={12} weight="fill" className="text-amber-400"/> Coordinator reviews</p>
                    {summary.total > 0 ? (
                        <div className="flex items-baseline gap-3 mt-1">
                            <span className="font-heading text-3xl data-num">{summary.avg}</span>
                            <span className="text-xs text-foreground/65">/5 · {summary.total} review{summary.total === 1 ? "" : "s"}</span>
                        </div>
                    ) : (
                        <p className="text-sm text-foreground/60 mt-1">
                            {canReview
                                ? "No reviews yet. Be the first to share your experience."
                                : "No reviews yet. Reviews are submitted by verified nonprofit event coordinators who have worked with this auctioneer."}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {isSuper && (
                        <button onClick={() => setSeedOpen(true)}
                                className="border border-accent text-accent px-3 py-2 text-[11px] uppercase tracking-wider font-bold inline-flex items-center gap-1.5 hover:bg-accent/10"
                                data-testid="seed-reviews-btn">
                            <Plus size={12} weight="bold"/> Seed reviews (admin)
                        </button>
                    )}
                    {canReview && (
                        <button onClick={onOpenWriteReview} className="bg-primary text-primary-foreground px-4 py-2 text-xs uppercase tracking-wider font-bold inline-flex items-center gap-2" data-testid="write-review-btn">
                            <Star size={12} weight="fill"/> Write a review
                        </button>
                    )}
                </div>
            </div>

            {summary.total > 0 && (
                <div className="space-y-1 mb-5">
                    {[5,4,3,2,1].map(n => {
                        const c = summary.distribution?.[n] || summary.distribution?.[String(n)] || 0;
                        const pct = summary.total ? Math.round((c / summary.total) * 100) : 0;
                        return (
                            <div key={n} className="flex items-center gap-2 text-[11px]">
                                <span className="w-4">{n}</span>
                                <Star size={10} weight="fill" className="text-amber-400"/>
                                <div className="flex-1 h-1.5 bg-secondary/40">
                                    <div className="h-full bg-amber-400" style={{ width: `${pct}%` }}/>
                                </div>
                                <span className="w-8 text-right text-foreground/55">{c}</span>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="space-y-4" data-testid="reviews-list">
                {reviews.map(rv => (
                    <article key={rv.id} className="border-t border-border pt-4" data-testid={`review-${rv.id}`}>
                        <div className="flex items-baseline justify-between gap-2 flex-wrap">
                            <h4 className="font-medium text-sm">{rv.headline}</h4>
                            <StarRow value={rv.rating} size={12}/>
                        </div>
                        <p className="text-xs text-foreground/55 mt-1">
                            <strong className="text-foreground/80">{rv.reviewer_display_name || rv.org_name || "Nonprofit coordinator"}</strong>
                            {" · "}{new Date(rv.created_at).toLocaleDateString(undefined, { month: "short", year: "numeric" })}
                        </p>
                        <div className="mt-1.5">
                            <VerificationBadge tier={rv.verification_tier} event_name={rv.event_name} event_date={rv.event_date}/>
                        </div>
                        {rv.body && <p className="text-sm mt-3 whitespace-pre-line leading-relaxed">{rv.body}</p>}
                        {rv.auctioneer_reply && (
                            <div className="bg-secondary/20 border-l-4 border-accent p-3 mt-3 text-xs" data-testid="auctioneer-reply">
                                <p className="font-medium text-accent flex items-center gap-1.5">
                                    <ChatCircle size={11} weight="fill"/> Response from {rv.auctioneer_reply_by || company}
                                </p>
                                <p className="text-foreground/80 mt-1 whitespace-pre-line leading-relaxed">{rv.auctioneer_reply}</p>
                            </div>
                        )}
                    </article>
                ))}
            </div>

            {!canReview && summary.total === 0 && (
                <p className="text-[11px] text-foreground/55 italic mt-2">
                    Only verified nonprofit event coordinators can review auctioneers. <Link to="/login" className="text-accent underline">Log in</Link> to leave a review.
                </p>
            )}

            {seedOpen && (
                <SeedReviewsModal slug={slug} company={company}
                                  onClose={() => setSeedOpen(false)}
                                  onSeeded={() => { setSeedOpen(false); onSeeded && onSeeded(); }}/>
            )}
        </div>
    );
}

/**
 * Superadmin-only modal: bulk-insert verified coordinator reviews onto this
 * auctioneer's profile. Pre-filled with the Larry Scala set so a single click
 * seeds the standard 4. Rows are fully editable.
 */
const LARRY_SCALA_DEFAULT_REVIEWS = [
    { reviewer_display_name: "C. Pierson", org_name: "Big Brothers Big Sisters", rating: 5, days_ago: 45,
      headline: "Larry made our gala feel effortless",
      body: "Larry has the room from the moment he steps on stage. Our paddle raise blew past goal — he reads donors like a book and keeps energy high without ever feeling pushy. Booking him again." },
    { reviewer_display_name: "J. Avila", org_name: "The Art Station", rating: 5, days_ago: 90,
      headline: "Warm, witty, and incredibly prepared",
      body: "Larry took the time to learn our mission and the families we serve. That showed on stage. Donors connected with him, and our fund-a-need was our best ever. Highly recommend." },
    { reviewer_display_name: "C. Permann", org_name: "Venture Club", rating: 5, days_ago: 150,
      headline: "Best benefit auctioneer we've worked with",
      body: "Professional from the first call through load-out. Larry coached our committee, suggested smart packages, and worked the room beautifully on event night. Worth every dollar." },
    { reviewer_display_name: "M. Zimmer", org_name: "Venture Club", rating: 5, days_ago: 210,
      headline: "Funny, classy, and gets results",
      body: "Larry strikes the perfect balance — entertaining enough to keep the crowd engaged, classy enough to honor our donors. Our live auction totals jumped significantly with him at the mic." },
];

function SeedReviewsModal({ slug, company, onClose, onSeeded }) {
    const isLarry = /larry.*scala/i.test(company || "") || /larry-scala/.test(slug || "");
    const [rows, setRows] = useState(isLarry ? LARRY_SCALA_DEFAULT_REVIEWS : [
        { reviewer_display_name: "", org_name: "", rating: 5, days_ago: 60, headline: "", body: "" },
    ]);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(null);

    const updateRow = (idx, patch) => setRows(rs => rs.map((r, i) => i === idx ? { ...r, ...patch } : r));
    const addRow = () => setRows(rs => [...rs, { reviewer_display_name: "", org_name: "", rating: 5, days_ago: 60, headline: "", body: "" }]);
    const removeRow = (idx) => setRows(rs => rs.filter((_, i) => i !== idx));
    const loadLarryDefaults = () => setRows(LARRY_SCALA_DEFAULT_REVIEWS);

    const submit = async () => {
        setErr("");
        const cleaned = rows.filter(r => r.reviewer_display_name?.trim() && r.org_name?.trim() && r.headline?.trim());
        if (cleaned.length === 0) { setErr("Add at least one review with name, org, and headline."); return; }
        setBusy(true);
        try {
            const r = await api.post(`/auctioneers/${slug}/admin/seed-reviews`, {
                verification_tier: "coordinator",
                reviews: cleaned.map(c => ({
                    reviewer_display_name: c.reviewer_display_name.trim(),
                    org_name: c.org_name.trim(),
                    rating: Number(c.rating) || 5,
                    headline: c.headline.trim(),
                    body: (c.body || "").trim() || null,
                    days_ago: Number(c.days_ago) || 60,
                })),
            });
            setDone(r.data);
            setTimeout(() => { onSeeded && onSeeded(); }, 900);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto p-4" onClick={onClose} data-testid="seed-reviews-modal">
            <div className="bg-card border border-border max-w-3xl w-full p-6 space-y-4 my-8" onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <p className="label-eyebrow text-accent flex items-center gap-1.5"><Plus size={12} weight="bold"/> Seed coordinator reviews</p>
                        <h2 className="font-heading text-2xl mt-1">For {company}</h2>
                        <p className="text-xs text-foreground/65 mt-1">Bulk-insert verified coordinator reviews. Re-running with the same reviewer + org updates the existing row (idempotent).</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-secondary" data-testid="seed-modal-close"><X size={16}/></button>
                </div>

                {done ? (
                    <div className="bg-green-50 border border-green-300 p-4 text-sm" data-testid="seed-success">
                        <p className="font-medium text-green-800">Done — {done.inserted} inserted, {done.updated} updated.</p>
                        <p className="text-green-700 text-xs mt-1">Reviews are now live on this profile. Closing…</p>
                    </div>
                ) : (
                    <>
                        {isLarry && (
                            <button type="button" onClick={loadLarryDefaults}
                                    className="text-[11px] text-accent underline hover:text-primary"
                                    data-testid="seed-load-larry">
                                ↻ Re-load Larry Scala default set (4 reviews)
                            </button>
                        )}

                        <div className="space-y-3 max-h-[55vh] overflow-y-auto">
                            {rows.map((row, idx) => (
                                <div key={idx} className="border border-border p-3 bg-white space-y-2" data-testid={`seed-row-${idx}`}>
                                    <div className="flex items-center justify-between">
                                        <p className="text-[10px] uppercase tracking-wider text-foreground/55">Review #{idx + 1}</p>
                                        {rows.length > 1 && (
                                            <button onClick={() => removeRow(idx)} className="text-foreground/55 hover:text-destructive p-1" data-testid={`seed-remove-${idx}`}><Trash size={12}/></button>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <input className="border border-border px-2 py-1.5 text-sm" placeholder="Reviewer name (e.g., C. Pierson)"
                                               value={row.reviewer_display_name}
                                               onChange={e => updateRow(idx, { reviewer_display_name: e.target.value })}/>
                                        <input className="border border-border px-2 py-1.5 text-sm" placeholder="Org (e.g., Big Brothers Big Sisters)"
                                               value={row.org_name}
                                               onChange={e => updateRow(idx, { org_name: e.target.value })}/>
                                    </div>
                                    <input className="border border-border px-2 py-1.5 text-sm w-full" placeholder="Headline (4-120 chars)"
                                           value={row.headline}
                                           onChange={e => updateRow(idx, { headline: e.target.value })}/>
                                    <textarea className="border border-border px-2 py-1.5 text-sm w-full" rows={3} placeholder="Body (optional, up to 3000 chars)"
                                              value={row.body}
                                              onChange={e => updateRow(idx, { body: e.target.value })}/>
                                    <div className="flex gap-2 text-xs items-center">
                                        <label className="flex items-center gap-1.5">Rating:
                                            <select value={row.rating} onChange={e => updateRow(idx, { rating: Number(e.target.value) })} className="border border-border px-1.5 py-1">
                                                {[5,4,3,2,1].map(n => <option key={n} value={n}>{n}★</option>)}
                                            </select>
                                        </label>
                                        <label className="flex items-center gap-1.5">Days ago:
                                            <input type="number" min="0" max="3650" value={row.days_ago}
                                                   onChange={e => updateRow(idx, { days_ago: e.target.value })}
                                                   className="border border-border px-1.5 py-1 w-20"/>
                                        </label>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <button type="button" onClick={addRow} className="border border-border px-3 py-1.5 text-xs uppercase tracking-wider inline-flex items-center gap-1.5 hover:bg-secondary/40" data-testid="seed-add-row">
                            <Plus size={12} weight="bold"/> Add another review
                        </button>

                        {err && <p className="text-destructive text-xs" data-testid="seed-err">{err}</p>}

                        <div className="flex justify-end gap-2 pt-2 border-t border-border">
                            <button onClick={onClose} disabled={busy} className="border border-border px-4 py-2 text-xs uppercase tracking-wider" data-testid="seed-cancel">Cancel</button>
                            <button onClick={submit} disabled={busy}
                                    className="bg-primary text-primary-foreground px-5 py-2 text-xs uppercase tracking-wider font-bold disabled:opacity-50"
                                    data-testid="seed-submit">
                                {busy ? "Seeding…" : `Seed ${rows.filter(r => r.reviewer_display_name && r.org_name && r.headline).length} review(s)`}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function WriteReviewModal({ slug, company, onClose, onSubmitted }) {
    const [rating, setRating] = useState(0);
    const [headline, setHeadline] = useState("");
    const [body, setBody] = useState("");
    const [eventId, setEventId] = useState("");
    const [pastEvents, setPastEvents] = useState([]);
    const [existing, setExisting] = useState(null);
    const [tierIfUnlinked, setTierIfUnlinked] = useState("unverified");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        api.get(`/auctioneer-reviews/my-eligibility/${slug}`).then(r => {
            setPastEvents(r.data?.past_events || []);
            setTierIfUnlinked(r.data?.verification_tier_if_unlinked || "unverified");
            if (r.data?.existing_review) {
                setExisting(r.data.existing_review);
                setRating(r.data.existing_review.rating || 0);
                setHeadline(r.data.existing_review.headline || "");
                setBody(r.data.existing_review.body || "");
                setEventId(r.data.existing_review.event_id || "");
            }
        }).catch(() => {});
    }, [slug]);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (rating < 1) { setErr("Please pick a star rating."); return; }
        if (!headline.trim() || headline.length < 4) { setErr("Headline must be at least 4 characters."); return; }
        setBusy(true);
        try {
            await api.post(`/auctioneers/${slug}/reviews`, {
                rating, headline: headline.trim(), body: body.trim() || null,
                event_id: eventId || null,
            });
            trackEvent("auctioneer_review_submitted", { auctioneer_slug: slug, rating, has_event_link: !!eventId });
            await onSubmitted();
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
            <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-card border border-border max-w-lg w-full p-6 space-y-4 my-8" data-testid="write-review-modal">
                <div className="flex items-start justify-between">
                    <div>
                        <p className="label-eyebrow text-accent flex items-center gap-1.5"><Star size={12} weight="fill"/> {existing ? "Update your review" : "Write a review"}</p>
                        <h2 className="font-heading text-2xl mt-1">{company}</h2>
                    </div>
                    <button type="button" onClick={onClose}><X size={20}/></button>
                </div>

                <div>
                    <p className="text-xs label-eyebrow mb-1.5">Your rating</p>
                    <div className="flex items-center gap-1">
                        {[1,2,3,4,5].map(n => (
                            <button type="button" key={n} onClick={() => setRating(n)} className="hover:scale-110 transition-transform" data-testid={`write-star-${n}`}>
                                <Star size={28} weight={n <= rating ? "fill" : "regular"} className={n <= rating ? "text-amber-400" : "text-foreground/30"}/>
                            </button>
                        ))}
                        {rating > 0 && <span className="text-sm text-foreground/70 ml-2">{rating}/5</span>}
                    </div>
                </div>

                <label className="block">
                    <span className="text-xs label-eyebrow">Headline <span className="text-destructive">*</span></span>
                    <input value={headline} onChange={e => setHeadline(e.target.value)} maxLength={120} placeholder="e.g. Raised 30% more than last year's gala" className="ed-input w-full mt-1" required data-testid="write-headline"/>
                </label>

                <label className="block">
                    <span className="text-xs label-eyebrow">Your review (optional, max 3000 chars)</span>
                    <textarea value={body} onChange={e => setBody(e.target.value)} maxLength={3000} rows={5} placeholder="What went well? Anything you'd tell another nonprofit considering this auctioneer?" className="ed-input w-full mt-1" data-testid="write-body"/>
                </label>

                {pastEvents.length > 0 && (
                    <label className="block">
                        <span className="text-xs label-eyebrow">Link this review to a specific event (optional, but strongest verification)</span>
                        <select value={eventId} onChange={e => setEventId(e.target.value)} className="ed-input w-full mt-1" data-testid="write-event-link">
                            <option value="">— Don't link to a specific event —</option>
                            {pastEvents.map(ev => (
                                <option key={ev.id} value={ev.id}>{ev.title} · {new Date(ev.start_date).toLocaleDateString()}</option>
                            ))}
                        </select>
                        <span className="text-[10px] text-foreground/55 block mt-1">
                            Linking adds a green "Verified — worked {pastEvents[0]?.title}" badge to your review.
                        </span>
                    </label>
                )}

                {pastEvents.length === 0 && tierIfUnlinked === "unverified" && (
                    <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 p-2">
                        Your review will publish as "Unverified" because we don't see any past events in your account yet. Create + run an event to earn the Verified Coordinator badge.
                    </p>
                )}

                {err && <p className="text-destructive text-sm" data-testid="write-error">{err}</p>}
                <button type="submit" disabled={busy} className="w-full bg-primary text-primary-foreground px-5 py-3 text-xs uppercase tracking-wider font-bold disabled:opacity-50" data-testid="write-submit">
                    {busy ? "Publishing…" : (existing ? "Update review" : "Publish review")}
                </button>
                <p className="text-[10px] text-foreground/55 text-center">
                    Reviews auto-publish. Bad-faith reviews can be hidden by our moderators. The auctioneer will be notified.
                </p>
            </form>
        </div>
    );
}
